import { CommonModule } from '@angular/common';
import { Component, Injector, OnInit } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { Cert } from '../../../../model/certs.model';
import { AuthService } from '../../../../services/auth.service';
import {convert_sha256, DEBOUNCE_TIME, extractErrorMessage, goBack, goToNextPage} from '../../../../common/common';
import {ApiService, CustomHttpParams} from 'src/app/services/api.service';
import {PaginationClass, PaginationModel} from "../../../../common/pagination";
import {NgxPaginationModule} from "ngx-pagination";
import {LoaderComponent} from "../../../common/loader/loader.component";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {debounceTime, distinctUntilChanged} from "rxjs/operators";
import {BehaviorSubject, Subject, takeUntil} from "rxjs";

@Component({
  selector: "app-cert-index",
  standalone: true,
  imports: [RouterModule, CommonModule, NgxPaginationModule, LoaderComponent, ReactiveFormsModule, FormsModule],
  templateUrl: "./cert-index.component.html",
  styleUrl: "./cert-index.component.scss",
})
export class CertIndexComponent implements OnInit {
  public isAdmin: boolean = false;
  public displayedCerts: Cert[] = [];
  public error: string | null = null;
  public router: Router;
  protected isLoading: boolean = false;

  protected readonly paginationId = 'certsPagination';
  private pagination = new PaginationClass(this.paginationId);
  protected paginationModel: PaginationModel = this.pagination.getPaginationModel();

  protected venIdSearch: string = '';
  private searchParams$ = new BehaviorSubject<any>({
    venId: '',
  });
  private unsubscribe = new Subject<void>();

  constructor(
    private injector: Injector,
    private authService: AuthService,
    private certService: ApiService
  ) {
    this.router = injector.get(Router);
  }

  ngOnInit(): void {
    this.isAdmin = this.authService.isAdmin();
    this.paginationModel = this.pagination.resetPagination();

    // Setup debounced search
    this.searchParams$.pipe(
      debounceTime(DEBOUNCE_TIME),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      takeUntil(this.unsubscribe)
    ).subscribe(() => {
      this.loadCerts();
    });
  }

  triggerSearch() {
    this.paginationModel = this.pagination.resetPagination();
    this.searchParams$.next({
      venId: this.venIdSearch,
    });
  }

  loadCerts() {
    this.isLoading = true;
    const queryParams: CustomHttpParams = {
      'depth': 1,
      'limit': this.pagination.itemsPerPage,
      'offset': this.pagination.offset,
    };
    if (this.venIdSearch) {
      queryParams['ven_id'] = this.venIdSearch.trim();
    }
    this.certService.findCert(queryParams).subscribe({
      next: (response: Cert[]) => {
        this.paginationModel = this.pagination.updateTotalItems(response.length);
        // Process certificates and extract fingerprints
        this.displayedCerts = response.map((certResponse: Cert) => {
          // Convert both primary and secondary certificates if present
          const fingerprints = convert_sha256(
            certResponse.public_cert,
            certResponse.secondary_public_cert
          );

          return {
            ...certResponse,
            cert_fingerprint: fingerprints.primary,
            secondary_cert_fingerprint: fingerprints.secondary
          };
        });
        this.isLoading = false;
      },
      error: (err) => {
        console.error(err);
        this.error = extractErrorMessage(err);
        this.isLoading = false;
      }
    });
  }

  nextPage(event: any, url: string, router: Router) {
    return goToNextPage(event, url, router);
  }

  delete(cert: any) {
    if (confirm("Deleting a cert for a VEN that is currently registered will cause the registration-index to be cancelled. Are you sure you want to proceed?")) {
      this.certService.deleteCert(cert?.ven_id).subscribe({
        next: () => {
          this.loadCerts(); // Reload the list after deletion
        },
        error: (err) => {
          console.error(err);
          this.error = "Failed to delete certificate.";
        }
      });
    }
  }

  handlePageChange(newPage: number) {
    this.paginationModel = this.pagination.changePage(newPage);
    this.loadCerts();
  }

  isDeletable(cert: any): boolean {
    // Logic to determine if a cert can be deleted
    // In the AngularJS version this always returned true
    return true;
  }
}
